import { types, applySnapshot, getSnapshot, flow } from 'mobx-state-tree';

import PayOnDeliveryAuth from 'js/services/auth-driver/PayOnDeliveryAuth';
import PayOnDeliveryReg from 'js/services/auth-driver/PayOnDeliveryReg';
import PayOnDeliveryMktReg from 'js/services/auth-driver/PayOnDeliveryMktReg';
import FacebookAuth from 'js/services/auth-driver/FacebookAuth';
import GoogleAuth from 'js/services/auth-driver/GoogleAuth';
import analytics from 'js/services/Analytics';
import Address from './types/Address';
import AppState from './AppState';
import PSP from "js/stores/types/PSP";

const authDrivers = {
  auth: PayOnDeliveryAuth,
  register: PayOnDeliveryReg,
  mktRegister: PayOnDeliveryMktReg,
  facebook: FacebookAuth,
  google: GoogleAuth
};

export default types
  .model('AppUser', {
    id: types.maybeNull(types.number),
    is_active: types.optional(types.union(types.number, types.boolean), false),
    is_confirmed: types.optional(types.union(types.number, types.boolean), false),
    is_registered: false,
    balance: 0.0,
    roles: types.array(types.string),
    login: '',
    email: '',
    name: types.maybeNull(types.string),
    first_name: types.maybeNull(types.string),
    last_name: types.maybeNull(types.string),
    phone_number: types.maybeNull(types.string),
    phone_number_formatted: types.maybeNull(types.string),
    notification_method: types.maybeNull(types.string),
    isLoggingOut: false,
    isLoggingIn: false,
    showVerificationCode: false,
    mseller: '',
    store_url: '',
    // Add the address type which will contain a street, city, state, zip, and country

    address: types.optional(Address, {}),
    avatar: '',
    psp: types.optional(PSP,{}),
  })
  .views(self => ({
    get isLoggedIn() {
      return !!self.id;
    },
    get isBusy() {
      return self.isLoggingIn || self.isLoggingOut;
    },
    get firstName() {
      return self.first_name;
    },
    hasRole(role = null) {
      return self.roles.includes(role);
    },
    hasRoles(roles = []) {
      return roles.reduce((hasRoles, role) => {
        return hasRoles && self.hasRole(role);
      }, true);
    },
  }))
  .actions(self => ({
    afterCreate() {
      if (window.app.env !== 'production') {
        // window.AppUser = self;
      }
      window.AppUser = self;
    },

    setIsRegistered() {
      self.is_registered = true;
    },

    setIsLoggingIn(isLoggingIn) {
      self.isLoggingIn = isLoggingIn;
    },

    setAvatar(avatar){
      self.avatar = avatar;
    },

    loginUser: flow(function* loginUser(scope = 'email', params = {}, driver = 'auth') {
      let response = null;
      if (!self.isLoggingIn) {
        self.isLoggingIn = true;
        try {
          response = authDrivers[driver].processLoginResponse(yield authDrivers[driver].login(scope, params));
          if (response) {
            self.handleResponse(driver, response);
          }
        } catch (error) {
          self.isLoggingIn = false;
          throw error;
        }
      }
      return response;
    }),

    logoutUser: flow(function* logoutUser() {
      if (self.isLoggedIn) {
        self.isLoggingOut = true;
        try {
          const logoutResponse = yield authDrivers.auth.logout();
          //analytics.track('Logout');
          analytics.reset();
          window.app.csrf_token = logoutResponse.data.csrf_token;
          applySnapshot(self, {});

          window.location.reload()
        } catch (error) {
          self.isLoggingOut = false;
          throw error;
        }
      }
    }),

    handleResponse(driver, response) {
      /*
      Handle the 4 chars verification code
      */
      if ( !response.is_registered && true === response.confirmation ) {
        AppState.setShowConfirmationCode(true);
        AppState.setMSeller(response.mseller);
      }

      driver === 'register' && self.setIsRegistered();
      if (response.credit_card_details && Array.isArray(response.credit_card_details)) {
        response.credit_card_details = {};
      }
      response.is_active && applySnapshot(self, response);
      window.app.csrf_token = response.csrf_token;
      // window.app.accessToken = response.accessToken;
    },

    setDetails(details) {
      applySnapshot(self, { ...getSnapshot(self), ...details });
    },
  }))
  .create(window.app.currentUser || {});
